import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import {startDateOfMonth, subDate} from '@shared/dateUtils';
import {ERROR} from '@di-shared/const';
import React, {Suspense, useState} from 'react';
import clsx from 'clsx';
import useDrivingInfoKr, {getHistoryForGraph} from '@di-shared/features/history/driving-info/useDrivingInfo.kr';
import {formatNumberWithCommas} from '@di-shared/utils';
import BANNER_EFFICIENCY from '@di-shared/assets/img/banner/banner_efficiency.png';
import BANNER_ECO_TIP from '@di-shared/assets/img/banner/banner_eco_tip.png';
import BANNER_ECONOMIZE from '@di-shared/assets/img/banner/banner_economize.png';
import {mapValueToRange} from '@di-shared/utils/graph';
import {getYYYYMMFormat} from '@di-shared/api/di/utils';
import {ErrorBoundary} from 'react-error-boundary';
import {DrivingInfoFuelProps} from '@di-shared/features/history/driving-info/DrivingInfoFuel/index';
import useUnitConverter from '@di-shared/utils/useUnitConverter';
import useCldrDatetimeFormatter from '@di-shared/utils/useCldrDatetimeFormatter';

export const FUEL_EFFICIENCY_GRAPH_KR = {
  MIN_VALUE: 0,
  MAX_HEIGHT: 90,
  MIN_HEIGHT: 6,
  AREA_FULL: 100,
  AREA_HALF: 50,
  LENGTH: 3,
};

enum FuelGraph {
  SAME_VEHICLE,
  TOP_10,
  MY_VEHICLE,
}

export default function ({vin, selectedDate}: DrivingInfoFuelProps) {
  return (
    <ErrorBoundary fallback={<FallbackError selectedDate={selectedDate} />}>
      <Suspense fallback={<FallbackLoading />}>
        <Inner
          key={getYYYYMMFormat(selectedDate)}
          vin={vin}
          selectedDate={selectedDate}
        />
      </Suspense>
    </ErrorBoundary>
  );
}

function Inner({vin, selectedDate}: DrivingInfoFuelProps) {
  const {isSameFuelAgreeMonth, currentFuelEfficiency} = useDrivingInfoKr({
    vin,
    selectedDate,
  });

  // 연비 데이터 null 방어
  if (isSameFuelAgreeMonth === null || !currentFuelEfficiency) {
    return null;
  }

  // 가입 당월 화면
  if (isSameFuelAgreeMonth) {
    return null;
  }

  // No rank 화면
  if (currentFuelEfficiency.myRank === null) {
    return (
      <NoRank
        vin={vin}
        selectedDate={selectedDate}
      />
    );
  }

  // Default 화면
  return (
    <Default
      vin={vin}
      selectedDate={selectedDate}
    />
  );
}

function NoRank({vin, selectedDate}: DrivingInfoFuelProps) {
  const {t} = useInferableTranslation();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const {convertFuelEfficiencyValue, convertFuelEfficiencySymbol} = useUnitConverter();
  const {drivingData, verifiedFuelEfficiencies, currentFuelEfficiency, prevFuelEfficiency} = useDrivingInfoKr({
    vin,
    selectedDate,
  });

  if (!currentFuelEfficiency) {
    throw new Error('currentFuelEfficiency error');
  }

  const [selectedBarGraph, setSelectedBarGraph] = useState(getYYYYMMFormat(selectedDate));
  const availableFuelEfficiencies = getHistoryForGraph(verifiedFuelEfficiencies, selectedDate, FUEL_EFFICIENCY_GRAPH_KR.LENGTH);

  const isFuelEfficiencyExist = drivingData.avgFuelEfficiency !== 0;
  const isPrevFuelExist = !!prevFuelEfficiency;
  const fuelDiff = isPrevFuelExist ? currentFuelEfficiency.avgFuelEfficiency - prevFuelEfficiency.avgFuelEfficiency : 0;
  const isFuelDiffUp = isPrevFuelExist ? fuelDiff >= 0 : true;

  const maxFuel = Math.max(drivingData.avgFuelEfficiency, ...availableFuelEfficiencies.map((fuel) => fuel.avgFuelEfficiency));
  const averageFuelHeight = isFuelEfficiencyExist
    ? FUEL_EFFICIENCY_GRAPH_KR.AREA_FULL -
      mapValueToRange({
        value: drivingData.avgFuelEfficiency,
        minValue: FUEL_EFFICIENCY_GRAPH_KR.MIN_VALUE,
        maxValue: maxFuel,
        minRange: FUEL_EFFICIENCY_GRAPH_KR.MIN_HEIGHT,
        maxRange: FUEL_EFFICIENCY_GRAPH_KR.MAX_HEIGHT,
      })
    : FUEL_EFFICIENCY_GRAPH_KR.AREA_HALF;

  return (
    <div className="section_container">
      <div className="section_container_header">
        <h4 className="title_xsmall_b">
          {t('T10082_$month_$fuel', {
            month: CldrDatetimeFormatter.format(selectedDate, 'MMM'),
            fuel:
              formatNumberWithCommas(convertFuelEfficiencyValue(currentFuelEfficiency.avgFuelEfficiency)) +
              convertFuelEfficiencySymbol(currentFuelEfficiency.avgFuelEfficiency),
          })}
        </h4>
      </div>
      {isFuelEfficiencyExist && isPrevFuelExist && (
        <div className="statistics_compare">
          <span className="statistics_compare_key label_medium_r">
            {t('T10039_$month', {month: CldrDatetimeFormatter.format(subDate(selectedDate, {months: 1}), 'MMM')})}
          </span>
          <span
            className={clsx('statistics_compare_val', {
              type_up: isFuelDiffUp,
              type_down: !isFuelDiffUp,
            })}
          >
            <span
              className={clsx('ic', {ic_di_count_up: isFuelDiffUp, ic_di_count_down: !isFuelDiffUp})}
              aria-hidden="true"
            >
              <span className="blind">{isFuelDiffUp ? t('T10059') : t('T10058')}</span>
            </span>
            <span className="label_medium_b">
              {formatNumberWithCommas(convertFuelEfficiencyValue(Math.abs(fuelDiff)))}
              {convertFuelEfficiencySymbol(Math.abs(fuelDiff))}
            </span>
          </span>
        </div>
      )}
      <div className="statistics_graph_area efficiency_statistics">
        <div className="bar_graph">
          {availableFuelEfficiencies.map((fuel) => {
            if (!fuel.verifiedDate) {
              throw new Error('fuel verifiedDate parse error');
            }
            const isSelectedMonth = fuel.date === selectedBarGraph;
            const graphHeight = mapValueToRange({
              value: fuel.avgFuelEfficiency,
              minValue: FUEL_EFFICIENCY_GRAPH_KR.MIN_VALUE,
              maxValue: maxFuel,
              minRange: FUEL_EFFICIENCY_GRAPH_KR.MIN_HEIGHT,
              maxRange: FUEL_EFFICIENCY_GRAPH_KR.MAX_HEIGHT,
            });

            return (
              <div
                key={fuel.date}
                className={clsx('bar_graph_item', isSelectedMonth && 'is-active')}
                onClick={() => setSelectedBarGraph(fuel.date)}
              >
                {isSelectedMonth && <span className="speech_bubble">{formatNumberWithCommas(fuel.avgFuelEfficiency)}</span>}
                <span
                  className="graph_bar"
                  style={{height: `${graphHeight}px`}}
                />
                <span className="graph_title">{CldrDatetimeFormatter.format(fuel.verifiedDate, 'MMM')}</span>
              </div>
            );
          })}
          <div
            className="bar_graph_average"
            style={{top: `${averageFuelHeight}px`}}
          >
            <span className="average_line" />
            <span className="average_text_box">
              <span className="average_text">{t('T10083')}</span>
              <span className="average_value">{formatNumberWithCommas(drivingData.avgFuelEfficiency)}</span>
            </span>
          </div>
        </div>
      </div>
      {isFuelEfficiencyExist && (
        <div className="driving_banner">
          <div className="driving_banner_box">
            <span className="driving_banner_txtbox">
              <strong
                className="driving_banner_title body_medium_b"
                style={{whiteSpace: 'pre-wrap'}}
              >
                {t('T10197')}
              </strong>
              <span className="driving_banner_desc body_small_r">{currentFuelEfficiency.fuelEfficiencyTip}</span>
            </span>
            <span className="driving_banner_imgbox">
              <img
                className="driving_banner_img"
                alt=""
                src={BANNER_ECO_TIP}
              />
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

function Default({vin, selectedDate}: DrivingInfoFuelProps) {
  const {t} = useInferableTranslation();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const {convertFuelEfficiencyValue, convertFuelEfficiencySymbol} = useUnitConverter();
  const {drivingData, currentFuelEfficiency, prevFuelEfficiency} = useDrivingInfoKr({
    vin,
    selectedDate,
  });

  if (!currentFuelEfficiency) {
    throw new Error('currentFuelEfficiency error');
  }

  const [selectedBarGraph, setSelectedBarGraph] = useState(FuelGraph.MY_VEHICLE);

  const isFuelEfficiencyExist = drivingData.avgFuelEfficiency !== 0;
  const isPrevFuelExist = !!prevFuelEfficiency;
  const fuelDiff = isPrevFuelExist ? currentFuelEfficiency.avgFuelEfficiency - prevFuelEfficiency.avgFuelEfficiency : 0;
  const isFuelDiffUp = isPrevFuelExist ? fuelDiff >= 0 : true;
  const isTopFuel = currentFuelEfficiency.avgFuelEfficiency >= currentFuelEfficiency.svAvgFuelEfficiencyTop10;

  const maxFuel = Math.max(
    drivingData.avgFuelEfficiency,
    currentFuelEfficiency.avgFuelEfficiency,
    currentFuelEfficiency.svAvgFuelEfficiency,
    currentFuelEfficiency.svAvgFuelEfficiencyTop10
  );
  const sameVehicleFuelHeight = mapValueToRange({
    value: currentFuelEfficiency.svAvgFuelEfficiency,
    minValue: FUEL_EFFICIENCY_GRAPH_KR.MIN_VALUE,
    maxValue: maxFuel,
    minRange: FUEL_EFFICIENCY_GRAPH_KR.MIN_HEIGHT,
    maxRange: FUEL_EFFICIENCY_GRAPH_KR.MAX_HEIGHT,
  });
  const sameVehicleTop10FuelHeight = mapValueToRange({
    value: currentFuelEfficiency.svAvgFuelEfficiencyTop10,
    minValue: FUEL_EFFICIENCY_GRAPH_KR.MIN_VALUE,
    maxValue: maxFuel,
    minRange: FUEL_EFFICIENCY_GRAPH_KR.MIN_HEIGHT,
    maxRange: FUEL_EFFICIENCY_GRAPH_KR.MAX_HEIGHT,
  });
  const myVehicleFuelHeight = mapValueToRange({
    value: currentFuelEfficiency.avgFuelEfficiency,
    minValue: FUEL_EFFICIENCY_GRAPH_KR.MIN_VALUE,
    maxValue: maxFuel,
    minRange: FUEL_EFFICIENCY_GRAPH_KR.MIN_HEIGHT,
    maxRange: FUEL_EFFICIENCY_GRAPH_KR.MAX_HEIGHT,
  });
  const averageFuelHeight = isFuelEfficiencyExist
    ? FUEL_EFFICIENCY_GRAPH_KR.AREA_FULL -
      mapValueToRange({
        value: drivingData.avgFuelEfficiency,
        minValue: FUEL_EFFICIENCY_GRAPH_KR.MIN_VALUE,
        maxValue: maxFuel,
        minRange: FUEL_EFFICIENCY_GRAPH_KR.MIN_HEIGHT,
        maxRange: FUEL_EFFICIENCY_GRAPH_KR.MAX_HEIGHT,
      })
    : FUEL_EFFICIENCY_GRAPH_KR.AREA_HALF;

  return (
    <div className="section_container">
      <div className="section_container_header">
        <h4 className="title_xsmall_b">
          {t('T10082_$month_$fuel', {
            month: CldrDatetimeFormatter.format(selectedDate, 'MMM'),
            fuel:
              formatNumberWithCommas(convertFuelEfficiencyValue(currentFuelEfficiency.avgFuelEfficiency)) +
              convertFuelEfficiencySymbol(currentFuelEfficiency.avgFuelEfficiency),
          })}
        </h4>
      </div>
      {isFuelEfficiencyExist && isPrevFuelExist && (
        <div className="statistics_compare">
          <span className="statistics_compare_key label_medium_r">
            {t('T10039_$month', {month: CldrDatetimeFormatter.format(subDate(selectedDate, {months: 1}), 'MMM')})}
          </span>
          <span
            className={clsx('statistics_compare_val', {
              type_up: isFuelDiffUp,
              type_down: !isFuelDiffUp,
            })}
          >
            <span
              className={clsx('ic', {ic_di_count_up: isFuelDiffUp, ic_di_count_down: !isFuelDiffUp})}
              aria-hidden="true"
            >
              <span className="blind">{isFuelDiffUp ? t('T10059') : t('T10058')}</span>
            </span>
            <span className="label_medium_b">
              {formatNumberWithCommas(convertFuelEfficiencyValue(Math.abs(fuelDiff)))}
              {convertFuelEfficiencySymbol(Math.abs(fuelDiff))}
            </span>
          </span>
        </div>
      )}
      <div className="statistics_graph_area efficiency_statistics">
        <div className="bar_graph">
          <div
            className={clsx('bar_graph_item', selectedBarGraph === FuelGraph.SAME_VEHICLE && 'is-active')}
            onClick={() => setSelectedBarGraph(FuelGraph.SAME_VEHICLE)}
          >
            {selectedBarGraph === FuelGraph.SAME_VEHICLE && (
              <span className="speech_bubble">{formatNumberWithCommas(currentFuelEfficiency.svAvgFuelEfficiency)}</span>
            )}
            <span
              className="graph_bar"
              style={{height: `${sameVehicleFuelHeight}px`}}
            />
            <span className="graph_title">{t('T10084')}</span>
          </div>
          <div
            className={clsx('bar_graph_item', selectedBarGraph === FuelGraph.TOP_10 && 'is-active')}
            onClick={() => setSelectedBarGraph(FuelGraph.TOP_10)}
          >
            {selectedBarGraph === FuelGraph.TOP_10 && (
              <span className="speech_bubble">{formatNumberWithCommas(currentFuelEfficiency.svAvgFuelEfficiencyTop10)}</span>
            )}
            <span
              className="graph_bar"
              style={{height: `${sameVehicleTop10FuelHeight}px`}}
            />
            <span className="graph_title">{t('T10085_$percent', {percent: 10})}</span>
          </div>
          <div
            className={clsx('bar_graph_item', selectedBarGraph === FuelGraph.MY_VEHICLE && 'is-active')}
            onClick={() => setSelectedBarGraph(FuelGraph.MY_VEHICLE)}
          >
            {selectedBarGraph === FuelGraph.MY_VEHICLE && (
              <span className="speech_bubble">{formatNumberWithCommas(currentFuelEfficiency.avgFuelEfficiency)}</span>
            )}
            <span
              className="graph_bar"
              style={{height: `${myVehicleFuelHeight}px`}}
            />
            <span className="graph_title">{t('T10086')}</span>
          </div>
          <div
            className="bar_graph_average"
            style={{top: `${averageFuelHeight}px`}}
          >
            <span className="average_line" />
            <span className="average_text_box">
              <span className="average_text">{t('T10083')}</span>
              <span className="average_value">{formatNumberWithCommas(drivingData.avgFuelEfficiency)}</span>
            </span>
          </div>
        </div>
      </div>
      {isFuelEfficiencyExist && (
        <div className="driving_banner">
          <div className="driving_banner_box">
            <span className="driving_banner_txtbox">
              <strong
                className="driving_banner_title body_medium_b"
                style={{whiteSpace: 'pre-wrap'}}
              >
                {isTopFuel ? t('T10195_$percent', {percent: currentFuelEfficiency.myRank}) : t('T10197')}
              </strong>
              <span className="driving_banner_desc body_small_r">{currentFuelEfficiency.fuelEfficiencyTip}</span>
            </span>
            <span className="driving_banner_imgbox">
              <img
                className="driving_banner_img"
                alt=""
                src={isTopFuel ? BANNER_EFFICIENCY : BANNER_ECONOMIZE}
              />
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

function FallbackLoading() {
  const today = new Date();
  const {defaultFuelEfficiencySymbol} = useUnitConverter();
  const {t} = useInferableTranslation();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();

  return (
    <div className="section_container skeleton">
      <div className="section_container_header">
        <h4 className="title_xsmall_b">
          {t('T10082_$month_$fuel', {
            month: CldrDatetimeFormatter.format(today, 'MMM'),
            fuel: ERROR.DASH + defaultFuelEfficiencySymbol,
          })}
        </h4>
      </div>
      <div className="statistics_graph_area efficiency_statistics">
        <div className="bar_graph">
          <div className="bar_graph_item">
            <span
              className="graph_bar"
              style={{height: `${FUEL_EFFICIENCY_GRAPH_KR.MIN_HEIGHT}px`}}
            />
            <span className="graph_title">{t('T10084')}</span>
          </div>
          <div className="bar_graph_item">
            <span
              className="graph_bar"
              style={{height: `${FUEL_EFFICIENCY_GRAPH_KR.MIN_HEIGHT}px`}}
            />
            <span className="graph_title">{t('T10085_$percent', {percent: 10})}</span>
          </div>
          <div className="bar_graph_item is-active">
            <span className="speech_bubble">{ERROR.DASH}</span>
            <span
              className="graph_bar"
              style={{height: `${FUEL_EFFICIENCY_GRAPH_KR.MIN_HEIGHT}px`}}
            />
            <span className="graph_title">{t('T10086')}</span>
          </div>
          <div
            className="bar_graph_average"
            style={{height: `${FUEL_EFFICIENCY_GRAPH_KR.AREA_HALF}px`}}
          >
            <span className="average_line" />
            <span className="average_text_box">
              <span className="average_text">{t('T10083')}</span>
              <span className="average_value">{ERROR.DASH}</span>
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

function FallbackError({selectedDate}: {selectedDate: Date}) {
  const {t} = useInferableTranslation();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const {defaultFuelEfficiencySymbol} = useUnitConverter();
  const startDate = startDateOfMonth(selectedDate);

  return (
    <div className="section_container">
      <div className="section_container_header">
        <h4 className="title_xsmall_b">
          {t('T10082_$month_$fuel', {
            month: CldrDatetimeFormatter.format(startDate, 'MMM'),
            fuel: ERROR.DASH + defaultFuelEfficiencySymbol,
          })}
        </h4>
      </div>
      <div className="statistics_graph_area efficiency_statistics">
        <div className="bar_graph">
          <div className="bar_graph_item">
            <span
              className="graph_bar"
              style={{height: `${FUEL_EFFICIENCY_GRAPH_KR.MIN_HEIGHT}px`}}
            />
            <span className="graph_title">{t('T10084')}</span>
          </div>
          <div className="bar_graph_item">
            <span
              className="graph_bar"
              style={{height: `${FUEL_EFFICIENCY_GRAPH_KR.MIN_HEIGHT}px`}}
            />
            <span className="graph_title">{t('T10085_$percent', {percent: 10})}</span>
          </div>
          <div className="bar_graph_item is-active">
            <span className="speech_bubble">{ERROR.DASH}</span>
            <span
              className="graph_bar"
              style={{height: `${FUEL_EFFICIENCY_GRAPH_KR.MIN_HEIGHT}px`}}
            />
            <span className="graph_title">{t('T10086')}</span>
          </div>
          <div
            className="bar_graph_average"
            style={{height: `${FUEL_EFFICIENCY_GRAPH_KR.AREA_HALF}px`}}
          >
            <span className="average_line" />
            <span className="average_text_box">
              <span className="average_text">{t('T10083')}</span>
              <span className="average_value">{ERROR.DASH}</span>
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
